// Sprint INTADMIN INTADMIN.B1 (#2110) — internal admin shell layout.
//
// Persistent left sidebar + a header that is deliberately NOT the main app
// header, plus an always-visible "Back to app" link. The visual distinction
// is a requirement, not decoration: this area edits platform-wide config
// (pricing that every tenant sees), so an admin must never mistake it for
// the ordinary tenant-scoped app. Hence the amber banner treatment and the
// explicit "Internal Admin" label — the main app is purple/neutral.
//
// Sprint INTADMIN delivered one active panel (Pricing, #2111). Sprint
// TENANTS.B1 (#2337) activates the Tenants item; System Config and
// Observability remain disabled placeholders so the information
// architecture is visible from day one — they are <span>s, not <NavLink>s,
// so they are genuinely non-clickable rather than links with pointer-events
// turned off.

import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowLeft, DollarSign, Settings2, Building2, Activity } from 'lucide-react';
import { apiFetch } from '../../lib/api';

export const INTERNAL_BASE = '/internal';

interface NavItem {
  label: string;
  icon: typeof DollarSign;
  to?: string;        // present = active/clickable
}

// Order is the sprint's IA, not alphabetical.
const NAV_ITEMS: NavItem[] = [
  { label: 'Pricing',       icon: DollarSign, to: `${INTERNAL_BASE}/pricing` },
  { label: 'System Config', icon: Settings2 },
  { label: 'Tenants',       icon: Building2, to: `${INTERNAL_BASE}/tenants` },
  { label: 'Observability', icon: Activity },
];

// TENANTS.B1 (#2337) — total tenant count badge on the sidebar item.
// `?limit=1` keeps the payload trivial; only `total` is read from the
// response (the same shape InternalTenantsPage itself consumes). Fetched
// once per layout mount, independent of which internal panel is showing,
// since the sidebar (and this count) is visible on every /internal/* route.
// A failed fetch just leaves the badge unrendered — this is a nice-to-have
// count, not a gate, so there is nothing to fail closed on.
function useTenantCount(): number | null {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ total: number }>('/api/internal/tenants?limit=1')
      .then(res => { if (!cancelled) setCount(res.total); })
      .catch(() => { /* no-op — badge just stays hidden */ });
    return () => { cancelled = true; };
  }, []);

  return count;
}

export default function InternalAdminLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const tenantCount = useTenantCount();

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <aside
        data-testid="internal-admin-sidebar"
        style={{
          width:         240,
          flexShrink:    0,
          display:       'flex',
          flexDirection: 'column',
          minHeight:     '100vh',
          position:      'sticky',
          top:           0,
          // Deliberately darker/warmer than the main app sidebar.
          background:    '#1c1917',
          borderRight:   '1px solid rgba(245,158,11,0.25)',
        }}
      >
        <div style={{ padding: '20px 16px 16px' }}>
          <div
            style={{
              fontFamily:    'var(--font-display)',
              fontSize:      13,
              fontWeight:    700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color:         '#f59e0b',
            }}
          >
            Internal Admin
          </div>
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize:   12,
              color:      'rgba(255,255,255,0.45)',
              marginTop:  2,
            }}
          >
            Platform configuration
          </div>
        </div>

        <nav style={{ flex: 1, padding: '0 8px' }}>
          {NAV_ITEMS.map(({ label, icon: Icon, to }) => {
            if (!to) {
              return (
                <span
                  key={label}
                  data-testid={`internal-nav-disabled-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  aria-disabled="true"
                  title="Coming soon"
                  style={{
                    display:    'flex',
                    alignItems: 'center',
                    gap:        12,
                    padding:    '10px 12px',
                    borderRadius: 8,
                    color:      'rgba(255,255,255,0.3)',
                    fontFamily: 'var(--font-body)',
                    fontSize:   14,
                    cursor:     'not-allowed',
                  }}
                >
                  <Icon size={18} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{label}</span>
                  <span
                    style={{
                      fontSize:     10,
                      padding:      '2px 6px',
                      borderRadius: 4,
                      background:   'rgba(255,255,255,0.08)',
                      color:        'rgba(255,255,255,0.4)',
                      whiteSpace:   'nowrap',
                    }}
                  >
                    Soon
                  </span>
                </span>
              );
            }

            const active = pathname === to || pathname.startsWith(`${to}/`);
            // Only Tenants carries a count today — a badge for a static/
            // single-panel item like Pricing would have nothing meaningful
            // to show, so this stays keyed to the label rather than adding
            // a `badge` field every future NAV_ITEM would need to opt out of.
            const badgeCount = label === 'Tenants' ? tenantCount : null;
            return (
              <NavLink
                key={label}
                to={to}
                aria-current={active ? 'page' : undefined}
                style={{
                  display:      'flex',
                  alignItems:   'center',
                  gap:          12,
                  padding:      '10px 12px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontFamily:   'var(--font-body)',
                  fontSize:     14,
                  fontWeight:   500,
                  background:   active ? 'rgba(245,158,11,0.15)' : 'transparent',
                  color:        active ? '#fbbf24' : 'rgba(255,255,255,0.7)',
                }}
              >
                <Icon size={18} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{label}</span>
                {badgeCount !== null && (
                  <span
                    data-testid="internal-nav-tenants-count"
                    style={{
                      fontSize:     11,
                      fontWeight:   600,
                      padding:      '2px 7px',
                      borderRadius: 10,
                      background:   active ? 'rgba(251,191,36,0.25)' : 'rgba(255,255,255,0.1)',
                      color:        active ? '#fbbf24' : 'rgba(255,255,255,0.75)',
                      whiteSpace:   'nowrap',
                    }}
                  >
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div style={{ padding: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Link
            to="/dashboard"
            data-testid="internal-back-to-app"
            style={{
              display:        'flex',
              alignItems:     'center',
              gap:            8,
              color:          'rgba(255,255,255,0.6)',
              fontFamily:     'var(--font-body)',
              fontSize:       13,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={16} />
            Back to app
          </Link>
        </div>
      </aside>

      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <header
          data-testid="internal-admin-header"
          style={{
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'space-between',
            gap:            16,
            padding:        '12px 24px',
            // The unmistakable tell that you are not in the tenant app.
            background:     '#78350f',
            borderBottom:   '1px solid #b45309',
            color:          '#fef3c7',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                fontFamily:    'var(--font-display)',
                fontSize:      14,
                fontWeight:    700,
                letterSpacing: '0.04em',
              }}
            >
              Internal Admin
            </span>
            <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, opacity: 0.8 }}>
              Changes here affect all tenants
            </span>
          </div>
          <Link
            to="/dashboard"
            style={{
              display:        'flex',
              alignItems:     'center',
              gap:            6,
              color:          '#fef3c7',
              fontFamily:     'var(--font-body)',
              fontSize:       13,
              textDecoration: 'none',
            }}
          >
            <ArrowLeft size={15} />
            Back to app
          </Link>
        </header>

        <main style={{ flex: 1, padding: 24, minWidth: 0 }}>{children}</main>
      </div>
    </div>
  );
}
